import {
	handleTextSelection,
	speak,
	stopSpeaking,
	enableReadingGuide,
	disableReadingGuide,
	applyBodyZoom,
	applyContentAlignment,
	applyColorSaturation,
	applyLineHeight,
	applyLetterSpacing,
	applyFontSizing,
	scanPageStructure,
	createReadingMask,
	removeReadingMask,
	enableTooltips,
	disableTooltips,
	enableHighlightHover,
	disableHighlightHover,
	enableMagnifiImages,
	disableMagnifiImages,
	BASE_SELECTOR,
	enablePointerTrail,
	disablePointerTrail,
	applyBigCursorStyle,
	applyPointerTrailVariant,
	getPointerTrailVariant,
} from "@/lib/feature-style";
import { useEffect, useState } from "@wordpress/element";
export const featureConfig = {
	highlightLinks: {
		type: "style",
		css: `* a { 
		background: #FFFB00 !important;
		 color: #010101 !important; 
		 text-decoration: underline !important;
		 font-weight: bold !important;
		 }`,
	},

	screenReader: {
		type: "js",
		key: "screenReader",
		handler: () => {
			const isActive = getActiveFeatures().includes("screenReader");
			//console.log("ScreenReader handler called. Active?", isActive);
			if (isActive) {
				// Deactivate
				document.removeEventListener("mouseup", handleTextSelection);
				stopSpeaking(); // ensure any ongoing long read is halted
				speak("Screen reader deactivated"); // short confirmation
				const updated = getActiveFeatures().filter((f) => f !== "screenReader");
				saveActiveFeatures(updated);
			} else {
				// Activate
				document.addEventListener("mouseup", handleTextSelection);
				stopSpeaking(); // clear queue before starting feature
				speak("Screen reader active");
				const updated = [...getActiveFeatures(), "screenReader"];
				saveActiveFeatures(updated);
			}
		},
	},

	contentScaling: {
		type: "js",
		steps: [1, 1.1, 1.2, 1.3],
		key: "contentScaling",
		handler: () => {
			handleStepBasedFeature(
				"contentScaling",
				[1.1, 1.2, 1.3, 1.4],
				applyBodyZoom,
				() => {
					applyBodyZoom(-1);
				},
			);
		},
	},

	contrast: {
		type: "js",
		steps: [0, 1, 2, 3], // Added step 3 for reset
		key: "contrast",
		handler: () => {
			handleStepBasedFeature("contrast", [0, 1, 2], applyContrastMode, () => {
				removeStyle("accessibility-widget-style-contrast");
			});
		},
	},

	sidebarLanguage: ``,
	searchBar: `
		
	`,

	seizureSafeProfile: ``,
	visionImpairedProfile: ``,
	adhdFriendlyProfile: ``,
	cognitiveDisabilityProfile: ``,
	keyboardNavigationMotor: ``,
	blindUsersScreenReader: ``,
	dyslexia: ``,
	highlightTitles: {
		type: "style",
		css: `*:not(#accessibility-widget):not(#accessibility-widget *) h1,
	 *:not(#accessibility-widget):not(#accessibility-widget *) h2,
	 *:not(#accessibility-widget):not(#accessibility-widget *) h3,
	 *:not(#accessibility-widget):not(#accessibility-widget *) h4,
	 *:not(#accessibility-widget):not(#accessibility-widget *) h5,
	 *:not(#accessibility-widget):not(#accessibility-widget *) h6 {
		outline: 2px solid #F95738 !important;
		outline-offset: 2px !important;
	}`,
	},

	textMagnifier: ``,
	readableFont: {
		type: "style",
		css: `*:not(i):not(span){font-family:Arial, sans-serif !important;}`,
	},
	dyslexiaFriendly: {
		type: "style",
		css: `*:not(i):not(span){font-family:"OpenDyslexic", "Comic Sans MS", "Helvetica Neue", Helvetica, Arial, sans-serif !important;}`,
	},
	dictionary: {
		type: "js",
		key: "dictionary",
		handler: () => {
			// This is now handled in React component
			// Just toggle the active state for UI feedback
			const isActive = getActiveFeatures().includes("dictionary");

			if (isActive) {
				const updated = getActiveFeatures().filter((f) => f !== "dictionary");
				saveActiveFeatures(updated);
			} else {
				const updated = [...getActiveFeatures(), "dictionary"];
				saveActiveFeatures(updated);
			}
		},
	},
	adjustFontSizing: {
		type: "js",
		steps: [0, 1, 2, 3],
		key: "adjustFontSizing",
		handler: () => {
			handleStepBasedFeature(
				"adjustFontSizing",
				[0, 1, 2],
				applyFontSizing,
				() => {
					applyFontSizing(-1);
					removeStyle("accessibility-widget-style-fontSizing");
				},
			);
		},
	},
	adjustLineHeight: {
		type: "js",
		steps: [1, 2, 3, 4],
		key: "adjustLineHeight",
		handler: () => {
			handleStepBasedFeature(
				"adjustLineHeight",
				[1, 2, 3],
				applyLineHeight,
				() => {
					// Reset to default line height
					applyLineHeight(-1);
					removeStyle("accessibility-widget-style-adjustLineHeight");
				},
			);
		},
	},
	adjustLetterSpacing: {
		type: "js",
		steps: [1, 2, 3, 4],
		key: "adjustLetterSpacing",
		handler: () => {
			handleStepBasedFeature(
				"adjustLetterSpacing",
				[1, 2, 3],
				applyLetterSpacing,
				() => {
					applyLetterSpacing(-1);
					removeStyle("accessibility-widget-style-adjustLetterSpacing");
				},
			);
		},
	},
	contentAlignment: {
		type: "js",
		steps: [0, 1, 2, 3],
		key: "contentAlignment",
		handler: () => {
			handleStepBasedFeature(
				"contentAlignment",
				[0, 1, 2],
				applyContentAlignment,
				() => {
					removeStyle("accessibility-widget-style-contentAlignment");
				},
			);
		},
	},
	greyscale: {
		type: "style",
		css: `html { filter: grayscale(1) !important;  }`,
	},
	colorSaturation: {
		type: "js",
		steps: [0, 1, 2, 3],
		key: "colorSaturation",
		handler: () => {
			handleStepBasedFeature(
				"colorSaturation",
				[0, 1, 2],
				applyColorSaturation,
				() => {
					// Reset both html and body filters (legacy stored on body)
					if (document.documentElement) {
						document.documentElement.style.filter = "none";
					}
					document.body.style.filter = "none";
					removeStyle("accessibility-widget-style-colorSaturation"); // legacy style id (kept for safety)
				},
			);
		},
	},
	pageStructure: {
		type: "js",
		key: "pageStructure",
		handler: (callback) => {
			// Scan the page structure
			const pageData = scanPageStructure();

			// Store the data in localStorage for the app to access
			localStorage.setItem(
				"accessibility-widget-page-structure-data",
				JSON.stringify(pageData),
			);

			// If callback provided (from app.jsx), call it with the data
			if (callback && typeof callback === "function") {
				callback(pageData);
			}

			return pageData;
		},
	},
	voiceNavigation: ``,
	keyboardNavigation: ``,
	bigCursor: {
		type: "js",
		steps: [0, 1, 2, 3], // 0..2 sizes, 3rd click resets (handled by handleStepBasedFeature)
		key: "bigCursor",
		handler: () => {
			const sizes = ["sm", "md", "lg"]; // map step index -> size key
			const applySize = (stepIndex) => {
				// ensure feature marked active before applying style so reapply logic sees it
				let active = getActiveFeatures();
				if (!active.includes("bigCursor")) {
					saveActiveFeatures([...active, "bigCursor"]);
					active = getActiveFeatures();
				}
				const sizeKey = sizes[stepIndex] || "lg";
				try {
					localStorage.setItem("accessibility-widget-bigCursor-size", sizeKey);
				} catch {}
				const variant =
					localStorage.getItem("accessibility-widget-bigCursor-variant") ||
					"default";
				applyBigCursorStyle(variant);
			};
			handleStepBasedFeature(
				"bigCursor",
				// pass sizes array purely for length; indices consumed inside applySize
				sizes,
				applySize,
				() => {
					removeStyle("accessibility-widget-style-bigCursor");
					try {
						localStorage.removeItem("accessibility-widget-bigCursor-size");
					} catch {}
				},
			);
		},
	},

	readingMask: {
		type: "js",
		key: "readingMask",
		handler: () => {
			if (typeof document === "undefined") return;
			const existing = document.getElementById(
				"accessibility-widget-reading-mask-overlay",
			);
			if (existing) {
				// Disable
				removeReadingMask();
				const active = getActiveFeatures();
				saveActiveFeatures(active.filter((f) => f !== "readingMask"));
				return;
			}
			createReadingMask();
			const active = getActiveFeatures();
			saveActiveFeatures([...active, "readingMask"]);
		},
	},

	pauseAnimations: {
		type: "style",
		css: `*{transition:none !important;transition-property:none !important;transition-delay:0s !important;transition-duration:0s !important;animation:none !important;animation-delay:0s !important;animation-play-state:paused !important;animation-duration:0s !important;animation-iteration-count:0!important;}`,
	},
	readingGuide: {
		type: "js",
		key: "readingGuide",
		handler: () => {
			const isActive = getActiveFeatures().includes("readingGuide");

			if (isActive) {
				disableReadingGuide();
				saveActiveFeatures(
					getActiveFeatures().filter((f) => f !== "readingGuide"),
				);
			} else {
				enableReadingGuide();
				saveActiveFeatures([...getActiveFeatures(), "readingGuide"]);
			}
		},
	},

	hideImages: {
		type: "style",
		css: `img, picture, image{visibility:hidden !important;}`,
	},
	outlineFocus: {
		type: "style",
		css: `*:focus-visible, *:focus{outline-width:3px !important;}`,
	},
	highlightHover: {
		type: "js",
		key: "highlightHover",
		handler: () => {
			const isActive = getActiveFeatures().includes("highlightHover");
			if (isActive) {
				disableHighlightHover();
				const updated = getActiveFeatures().filter(
					(f) => f !== "highlightHover",
				);
				saveActiveFeatures(updated);
				return;
			}
			enableHighlightHover();
			const updated = [...getActiveFeatures(), "highlightHover"];
			saveActiveFeatures(updated);
		},
	},
	tooltips: {
		type: "js",
		key: "tooltips",
		handler: () => {
			const isActive = getActiveFeatures().includes("tooltips");
			if (isActive) {
				disableTooltips();
				const updated = getActiveFeatures().filter((f) => f !== "tooltips");
				saveActiveFeatures(updated);
				return;
			}
			enableTooltips();
			const updated = [...getActiveFeatures(), "tooltips"];
			saveActiveFeatures(updated);
		},
	},
	magnifiImages: {
		type: "js",
		key: "magnifiImages",
		handler: () => {
			const isActive = getActiveFeatures().includes("magnifiImages");
			if (isActive) {
				disableMagnifiImages();
				const updated = getActiveFeatures().filter(
					(f) => f !== "magnifiImages",
				);
				saveActiveFeatures(updated);
				return;
			}
			enableMagnifiImages();
			const updated = [...getActiveFeatures(), "magnifiImages"];
			saveActiveFeatures(updated);
		},
	},
	pointerTrail: {
		type: "js",
		key: "pointerTrail",
		steps: [0, 1, 2, 3, 4, 5], // 0..4 variants, 5th click resets
		handler: () => {
			const variants = [
				"trail",
				"neon",
				"particle",
				"rainbow",
				"geometric",
				"line",
			]; // last index reserved for reset logic
			const currentStep = getFeatureStep("pointerTrail");
			const next = currentStep === -1 ? 0 : currentStep + 1;
			if (next >= variants.length) {
				// reset
				disablePointerTrail();
				saveFeatureStep("pointerTrail", -1);
				saveActiveFeatures(
					getActiveFeatures().filter((f) => f !== "pointerTrail"),
				);
				try {
					localStorage.removeItem("accessibility-widget-pointerTrail-variant");
				} catch {}
				return;
			}
			// apply variant
			const variant = variants[next];
			applyPointerTrailVariant(variant === "trail" ? "base" : variant);
			saveFeatureStep("pointerTrail", next);
			const active = getActiveFeatures();
			if (!active.includes("pointerTrail")) {
				saveActiveFeatures([...active, "pointerTrail"]);
			} else {
				// ensure active list dispatches event for UI update
				saveActiveFeatures(active);
			}
		},
	},
	clickSpark: {
		type: "js",
		key: "clickSpark",
		handler: () => {
			const active = getActiveFeatures();
			const isActive = active.includes("clickSpark");
			if (isActive) {
				// remove canvas listener
				const existing = document.getElementById(
					"accessibility-widget-click-spark-canvas",
				);
				if (existing) existing.remove();
				const updated = active.filter((f) => f !== "clickSpark");
				saveActiveFeatures(updated);
				return;
			}
			// Activate: create a full-screen canvas capturing clicks
			const canvas = document.createElement("canvas");
			canvas.id = "accessibility-widget-click-spark-canvas";
			Object.assign(canvas.style, {
				position: "fixed",
				inset: 0,
				pointerEvents: "none",
				zIndex: 999997,
			});
			document.body.appendChild(canvas);
			const ctx = canvas.getContext("2d");
			const sparks = [];
			function resize() {
				canvas.width = window.innerWidth;
				canvas.height = window.innerHeight;
			}
			resize();
			window.addEventListener("resize", resize);
			const settings = (() => {
				try {
					return JSON.parse(
						localStorage.getItem("accessibility-widget-clickSpark-settings") ||
							"{}",
					);
				} catch {
					return {};
				}
			})();
			// Fallback: attempt to parse svgSettings payload (saved via Save button)
			let persistedCfg = {};
			try {
				const raw = localStorage.getItem(
					"accessibility-widget-settings-payload",
				);
				if (raw) {
					const parsed = JSON.parse(raw);
					persistedCfg = parsed?.svgSettings?.clickSpark || {};
				}
			} catch {}
			const cfg = {
				color: settings.color || persistedCfg.color || "#155eef",
				size: settings.size || persistedCfg.size || 10,
				radius: settings.radius || persistedCfg.radius || 15,
				count: settings.count || persistedCfg.count || 8,
				duration: settings.duration || persistedCfg.duration || 400,
				scale: settings.scale || persistedCfg.scale || 1,
				easing: settings.easing || persistedCfg.easing || "ease-out",
			};
			function ease(t) {
				switch (cfg.easing) {
					case "linear":
						return t;
					case "ease-in":
						return t * t;
					case "ease-in-out":
						return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
					default:
						return t * (2 - t);
				}
			}
			function addSparks(x, y) {
				const now = performance.now();
				for (let i = 0; i < cfg.count; i++) {
					sparks.push({
						x,
						y,
						angle: (Math.PI * 2 * i) / cfg.count,
						start: now,
					});
				}
			}
			function animate(ts) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
				for (let i = sparks.length - 1; i >= 0; i--) {
					const s = sparks[i];
					const el = ts - s.start;
					if (el > cfg.duration) {
						sparks.splice(i, 1);
						continue;
					}
					const p = el / cfg.duration;
					const eased = ease(p);
					const dist = eased * cfg.radius * cfg.scale;
					const lineLen = cfg.size * (1 - eased);
					const x1 = s.x + dist * Math.cos(s.angle);
					const y1 = s.y + dist * Math.sin(s.angle);
					const x2 = s.x + (dist + lineLen) * Math.cos(s.angle);
					const y2 = s.y + (dist + lineLen) * Math.sin(s.angle);
					ctx.strokeStyle = cfg.color;
					ctx.lineWidth = 2;
					ctx.beginPath();
					ctx.moveTo(x1, y1);
					ctx.lineTo(x2, y2);
					ctx.stroke();
				}
				requestAnimationFrame(animate);
			}
			function clickListener(e) {
				addSparks(e.clientX, e.clientY);
			}
			document.addEventListener("click", clickListener, true);
			requestAnimationFrame(animate);
			canvas.dataset.listener = "1";
			// store cleanup
			window.__clickSparkCleanup = () => {
				document.removeEventListener("click", clickListener, true);
				window.removeEventListener("resize", resize);
				if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
				delete window.__clickSparkCleanup;
			};
			const updated = [...active, "clickSpark"];
			saveActiveFeatures(updated);
		},
	},
	imageTrails: {
		type: "js",
		key: "imageTrails",
		handler: () => {
			const active = getActiveFeatures();
			const isActive = active.includes("imageTrails");
			if (isActive) {
				// Remove image trails
				const existing = document.getElementById(
					"accessibility-widget-image-trails-container",
				);
				if (existing) existing.remove();
				const updated = active.filter((f) => f !== "imageTrails");
				saveActiveFeatures(updated);
				return;
			}
			// Activate image trails
			const container = document.createElement("div");
			container.id = "accessibility-widget-image-trails-container";
			Object.assign(container.style, {
				position: "fixed",
				inset: 0,
				pointerEvents: "none",
				zIndex: 999996,
				overflow: "hidden",
			});
			document.body.appendChild(container);

			// Get settings from localStorage
			const settings = (() => {
				try {
					return JSON.parse(
						localStorage.getItem("accessibility-widget-imageTrails-settings") ||
							"{}",
					);
				} catch {
					return {};
				}
			})();

			const images = settings.images || [
				"https://picsum.photos/id/287/300/300",
				"https://picsum.photos/id/1001/300/300",
				"https://picsum.photos/id/1025/300/300",
				"https://picsum.photos/id/1026/300/300",
				"https://picsum.photos/id/1027/300/300",
				"https://picsum.photos/id/1028/300/300",
				"https://picsum.photos/id/1029/300/300",
				"https://picsum.photos/id/1028/300/300",
			];
			const variant = settings.variant || 1;

			let imgIndex = 0;
			const activeImages = [];
			const threshold = 50;
			let lastPos = { x: -1000, y: -1000 };

			// Create image pool
			const imagePool = images.map((src) => {
				const img = document.createElement("div");
				Object.assign(img.style, {
					position: "absolute",
					width: "60px",
					height: "60px",
					backgroundImage: `url(${src})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					borderRadius: "10px",
					opacity: 0,
					transition: "none",
					pointerEvents: "none",
				});
				return img;
			});

			function getDistance(p1, p2) {
				const dx = p1.x - p2.x;
				const dy = p1.y - p2.y;
				return Math.sqrt(dx * dx + dy * dy);
			}

			function showNextImage(x, y) {
				const img = imagePool[imgIndex % imagePool.length].cloneNode();
				imgIndex++;
				container.appendChild(img);

				// Position at mouse
				img.style.left = x - 30 + "px";
				img.style.top = y - 30 + "px";
				img.style.transform = "scale(0)";
				img.style.opacity = "1";

				// Animate based on variant
				setTimeout(() => {
					switch (variant) {
						case 1: // Fade and shrink
							img.style.transition = "all 0.5s ease-out";
							img.style.transform = "scale(1)";
							setTimeout(() => {
								img.style.opacity = "0";
								img.style.transform = "scale(0.2)";
							}, 200);
							break;
						case 2: // Scale up and fade
							img.style.transition = "all 0.6s ease-out";
							img.style.transform = "scale(1.5)";
							img.style.opacity = "0";
							break;
						case 3: // Float up
							img.style.transition = "all 0.8s ease-out";
							img.style.transform = "scale(1) translateY(-100px)";
							img.style.opacity = "0";
							break;
						case 4: // Spiral out
							img.style.transition = "all 1s ease-out";
							img.style.transform = "scale(1) rotate(360deg) translateX(50px)";
							img.style.opacity = "0";
							break;
						case 5: // Bounce
							img.style.transition =
								"all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)";
							img.style.transform = "scale(1)";
							setTimeout(() => {
								img.style.opacity = "0";
								img.style.transform = "scale(0) translateY(-50px)";
							}, 300);
							break;
						case 6: // Blur out
							img.style.transition = "all 0.7s ease-out";
							img.style.transform = "scale(1)";
							img.style.filter = "blur(0px)";
							setTimeout(() => {
								img.style.opacity = "0";
								img.style.filter = "blur(10px)";
								img.style.transform = "scale(1.2)";
							}, 200);
							break;
						case 7: // Slide trail
							img.style.transition = "all 0.8s ease-out";
							const angle = Math.random() * Math.PI * 2;
							const dist = 80;
							img.style.transform = `scale(1) translate(${
								Math.cos(angle) * dist
							}px, ${Math.sin(angle) * dist}px)`;
							img.style.opacity = "0";
							break;
						case 8: // 3D flip
							img.style.transition = "all 0.8s ease-out";
							img.style.transform = "scale(1) rotateY(0deg)";
							setTimeout(() => {
								img.style.opacity = "0";
								img.style.transform = "scale(0.5) rotateY(180deg)";
							}, 200);
							break;
					}
				}, 10);

				// Cleanup
				setTimeout(() => {
					if (img.parentNode) img.remove();
				}, 1200);
			}

			function handleMouseMove(e) {
				const currentPos = { x: e.clientX, y: e.clientY };
				const distance = getDistance(currentPos, lastPos);

				if (distance > threshold) {
					showNextImage(e.clientX, e.clientY);
					lastPos = currentPos;
				}
			}

			document.addEventListener("mousemove", handleMouseMove);

			// Store cleanup
			window.__imageTrailsCleanup = () => {
				document.removeEventListener("mousemove", handleMouseMove);
				if (container.parentNode) container.remove();
				delete window.__imageTrailsCleanup;
			};

			const updated = [...active, "imageTrails"];
			saveActiveFeatures(updated);
		},
	},
};

// --- Helper: ensure imageTrails exists if feature marked active (page reload case) ---
function createImageTrailsIfMissing() {
	if (typeof document === "undefined") return;
	if (document.getElementById("accessibility-widget-image-trails-container"))
		return; // already there

	// Reuse the handler logic
	const container = document.createElement("div");
	container.id = "accessibility-widget-image-trails-container";
	Object.assign(container.style, {
		position: "fixed",
		inset: 0,
		pointerEvents: "none",
		zIndex: 999996,
		overflow: "hidden",
	});
	document.body.appendChild(container);

	// Get settings from localStorage
	const settings = (() => {
		try {
			return JSON.parse(
				localStorage.getItem("accessibility-widget-imageTrails-settings") ||
					"{}",
			);
		} catch {
			return {};
		}
	})();

	const images = settings.images || [
		"https://picsum.photos/id/287/300/300",
		"https://picsum.photos/id/1001/300/300",
		"https://picsum.photos/id/1025/300/300",
		"https://picsum.photos/id/1026/300/300",
		"https://picsum.photos/id/1027/300/300",
		"https://picsum.photos/id/1028/300/300",
		"https://picsum.photos/id/1029/300/300",
		"https://picsum.photos/id/1028/300/300",
	];
	const variant = settings.variant || 1;

	let imgIndex = 0;
	const threshold = 50;
	let lastPos = { x: -1000, y: -1000 };

	// Create image pool
	const imagePool = images.map((src) => {
		const img = document.createElement("div");
		Object.assign(img.style, {
			position: "absolute",
			width: "60px",
			height: "60px",
			backgroundImage: `url(${src})`,
			backgroundSize: "cover",
			backgroundPosition: "center",
			borderRadius: "10px",
			opacity: 0,
			transition: "none",
			pointerEvents: "none",
		});
		return img;
	});

	function getDistance(p1, p2) {
		const dx = p1.x - p2.x;
		const dy = p1.y - p2.y;
		return Math.sqrt(dx * dx + dy * dy);
	}

	function showNextImage(x, y) {
		const img = imagePool[imgIndex % imagePool.length].cloneNode();
		imgIndex++;
		container.appendChild(img);

		// Position at mouse
		img.style.left = x - 30 + "px";
		img.style.top = y - 30 + "px";
		img.style.transform = "scale(0)";
		img.style.opacity = "1";

		// Animate based on variant
		setTimeout(() => {
			switch (variant) {
				case 1: // Fade and shrink
					img.style.transition = "all 0.5s ease-out";
					img.style.transform = "scale(1)";
					setTimeout(() => {
						img.style.opacity = "0";
						img.style.transform = "scale(0.2)";
					}, 200);
					break;
				case 2: // Scale up and fade
					img.style.transition = "all 0.6s ease-out";
					img.style.transform = "scale(1.5)";
					img.style.opacity = "0";
					break;
				case 3: // Float up
					img.style.transition = "all 0.8s ease-out";
					img.style.transform = "scale(1) translateY(-100px)";
					img.style.opacity = "0";
					break;
				case 4: // Spiral out
					img.style.transition = "all 1s ease-out";
					img.style.transform = "scale(1) rotate(360deg) translateX(50px)";
					img.style.opacity = "0";
					break;
				case 5: // Bounce
					img.style.transition =
						"all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)";
					img.style.transform = "scale(1)";
					setTimeout(() => {
						img.style.opacity = "0";
						img.style.transform = "scale(0) translateY(-50px)";
					}, 300);
					break;
				case 6: // Blur out
					img.style.transition = "all 0.7s ease-out";
					img.style.transform = "scale(1)";
					img.style.filter = "blur(0px)";
					setTimeout(() => {
						img.style.opacity = "0";
						img.style.filter = "blur(10px)";
						img.style.transform = "scale(1.2)";
					}, 200);
					break;
				case 7: // Slide trail
					img.style.transition = "all 0.8s ease-out";
					const angle = Math.random() * Math.PI * 2;
					const dist = 80;
					img.style.transform = `scale(1) translate(${
						Math.cos(angle) * dist
					}px, ${Math.sin(angle) * dist}px)`;
					img.style.opacity = "0";
					break;
				case 8: // 3D flip
					img.style.transition = "all 0.8s ease-out";
					img.style.transform = "scale(1) rotateY(0deg)";
					setTimeout(() => {
						img.style.opacity = "0";
						img.style.transform = "scale(0.5) rotateY(180deg)";
					}, 200);
					break;
			}
		}, 10);

		// Cleanup
		setTimeout(() => {
			if (img.parentNode) img.remove();
		}, 1200);
	}

	function handleMouseMove(e) {
		const currentPos = { x: e.clientX, y: e.clientY };
		const distance = getDistance(currentPos, lastPos);

		if (distance > threshold) {
			showNextImage(e.clientX, e.clientY);
			lastPos = currentPos;
		}
	}

	document.addEventListener("mousemove", handleMouseMove);

	// Store cleanup
	window.__imageTrailsCleanup = () => {
		document.removeEventListener("mousemove", handleMouseMove);
		if (container.parentNode) container.remove();
		delete window.__imageTrailsCleanup;
	};
}

// --- Helper: ensure clickSpark canvas exists if feature marked active (page reload case) ---
function createClickSparkIfMissing() {
	if (typeof document === "undefined") return;
	if (document.getElementById("accessibility-widget-click-spark-canvas"))
		return; // already there
	// Build settings (mirrors handler activation branch)
	const canvas = document.createElement("canvas");
	canvas.id = "accessibility-widget-click-spark-canvas";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		pointerEvents: "none",
		zIndex: 999997,
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const sparks = [];
	function resize() {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	}
	resize();
	window.addEventListener("resize", resize);
	let settings = {};
	try {
		settings = JSON.parse(
			localStorage.getItem("accessibility-widget-clickSpark-settings") || "{}",
		);
	} catch {}
	let persistedCfg = {};
	try {
		const raw = localStorage.getItem("accessibility-widget-settings-payload");
		if (raw) {
			const parsed = JSON.parse(raw);
			persistedCfg = parsed?.svgSettings?.clickSpark || {};
		}
	} catch {}
	const cfg = {
		color: settings.color || persistedCfg.color || "#155eef",
		size: settings.size || persistedCfg.size || 10,
		radius: settings.radius || persistedCfg.radius || 15,
		count: settings.count || persistedCfg.count || 8,
		duration: settings.duration || persistedCfg.duration || 400,
		scale: settings.scale || persistedCfg.scale || 1,
		easing: settings.easing || persistedCfg.easing || "ease-out",
	};
	function ease(t) {
		switch (cfg.easing) {
			case "linear":
				return t;
			case "ease-in":
				return t * t;
			case "ease-in-out":
				return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
			default:
				return t * (2 - t);
		}
	}
	function addSparks(x, y) {
		const now = performance.now();
		for (let i = 0; i < cfg.count; i++) {
			sparks.push({ x, y, angle: (Math.PI * 2 * i) / cfg.count, start: now });
		}
	}
	function animate(ts) {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		for (let i = sparks.length - 1; i >= 0; i--) {
			const s = sparks[i];
			const el = ts - s.start;
			if (el > cfg.duration) {
				sparks.splice(i, 1);
				continue;
			}
			const p = el / cfg.duration;
			const eased = ease(p);
			const dist = eased * cfg.radius * cfg.scale;
			const lineLen = cfg.size * (1 - eased);
			const x1 = s.x + dist * Math.cos(s.angle);
			const y1 = s.y + dist * Math.sin(s.angle);
			const x2 = s.x + (dist + lineLen) * Math.cos(s.angle);
			const y2 = s.y + (dist + lineLen) * Math.sin(s.angle);
			ctx.strokeStyle = cfg.color;
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
			ctx.stroke();
		}
		requestAnimationFrame(animate);
	}
	function clickListener(e) {
		addSparks(e.clientX, e.clientY);
	}
	document.addEventListener("click", clickListener, true);
	requestAnimationFrame(animate);
	canvas.dataset.listener = "1";
	window.__clickSparkCleanup = () => {
		document.removeEventListener("click", clickListener, true);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
		delete window.__clickSparkCleanup;
	};
}

// --- Style Injection ---
function injectStyle(id, css) {
	let style = document.getElementById(id);
	if (!style) {
		style = document.createElement("style");
		style.id = id;
		document.head.appendChild(style);
	}
	style.innerText = css;
}

function removeStyle(id) {
	const el = document.getElementById(id);
	if (el) el.remove();
}

// --- LocalStorage Helpers ---
function getActiveFeatures() {
	try {
		return JSON.parse(
			localStorage.getItem("accessibility-widget-active-features") || "[]",
		);
	} catch {
		return [];
	}
}

function saveActiveFeatures(features) {
	localStorage.setItem(
		"accessibility-widget-active-features",
		JSON.stringify(features),
	);
	// Notify all hook instances to sync
	if (typeof window !== "undefined") {
		window.dispatchEvent(new Event("accessibility-features-changed"));
	}
}

function getFeatureStep(key) {
	const data = JSON.parse(
		localStorage.getItem("accessibility-widget-feature-steps") || "{}",
	);
	// For all step-based features, return -1 if not set
	if (data[key] === undefined) {
		return -1;
	}
	return data[key];
}

function saveFeatureStep(key, step) {
	const data = JSON.parse(
		localStorage.getItem("accessibility-widget-feature-steps") || "{}",
	);
	data[key] = step;
	localStorage.setItem(
		"accessibility-widget-feature-steps",
		JSON.stringify(data),
	);
	if (typeof window !== "undefined") {
		window.dispatchEvent(new Event("accessibility-features-changed"));
	}
}

// --- Contrast Mode Styles ---
function applyContrastMode(stepIndex) {
	const id = "accessibility-widget-style-contrast";

	removeStyle(id);

	const styles = [
		`${BASE_SELECTOR} {
        box-shadow: none !important;
        background-image: none !important;
        text-shadow: none !important;
        background-color: #18181b !important;
    }
    ${BASE_SELECTOR}:not(a) {
        color: #fafafa !important;
    }
    a:not(.accessibility-widget-link) {
        color: #facc14 !important;
    }`,

		`${BASE_SELECTOR} {
        box-shadow: none !important;
        background-image: none !important;
        text-shadow: none !important;
        background-color: #fff !important;
    }
    ${BASE_SELECTOR}:not(a) {
        color: #000 !important;
    }
    a:not(.accessibility-widget-link) {
        color: rgb(0, 0, 211) !important;
    }`,

		`${BASE_SELECTOR} {
        box-shadow: none !important;
        background-image: none !important;
        text-shadow: none !important;
        background-color: #000 !important;
    }
    ${BASE_SELECTOR}:not(a) {
        color: #fff !important;
    }
    a:not(.accessibility-widget-link) {
        text-decoration: underline !important;
        color: #ffff00 !important;
    }`,
	];

	if (styles[stepIndex]) {
		injectStyle(id, styles[stepIndex]);
	}
}
function handleStepBasedFeature(key, steps, applyFn, removeFn) {
	const current = getFeatureStep(key);
	const next = current === -1 ? 0 : current + 1;

	if (next >= steps.length) {
		removeFn?.();
		saveFeatureStep(key, -1);
		saveActiveFeatures(getActiveFeatures().filter((f) => f !== key));
	} else {
		applyFn(next);
		saveFeatureStep(key, next);

		const active = getActiveFeatures();
		if (!active.includes(key)) {
			saveActiveFeatures([...active, key]);
		}
	}
}

// --- useFeatureToggle Hook ---
export function useFeatureToggle() {
	const [activeFeatures, setActiveFeatures] = useState(getActiveFeatures());
	const [activeProfile, setActiveProfile] = useState(() =>
		typeof window !== "undefined"
			? localStorage.getItem("accessibility-widget-active-profile") || null
			: null,
	);
	const [version, setVersion] = useState(0);
	useEffect(() => {
		const active = getActiveFeatures();
		//("Active features on load:", active);
		active.forEach((key) => {
			const config = featureConfig[key];

			if (config?.type === "style") {
				// bigCursor no longer style-based
				if (key !== "bigCursor") {
					injectStyle(`accessibility-widget-style-${key}`, config.css);
				}
			}

			if (config?.type === "js") {
				const step = getFeatureStep(key);
				if (key === "contentScaling" && step !== -1) {
					applyBodyZoom(step);
				}
				if (key === "contrast" && step !== -1) {
					applyContrastMode(step);
				}
				if (key === "contentAlignment" && step !== -1) {
					applyContentAlignment(step);
				}
				if (key === "colorSaturation" && step !== -1) {
					applyColorSaturation(step);
				}
				if (key === "bigCursor") {
					// Derive size from stored step if exists, else leave inactive styling
					const s = getFeatureStep("bigCursor");
					if (s !== -1) {
						// map 0,1,2 -> sizes
						const sizes = ["sm", "md", "lg"];
						const sizeKey = sizes[s] || "lg";
						try {
							localStorage.setItem(
								"accessibility-widget-bigCursor-size",
								sizeKey,
							);
						} catch {}
						const variant =
							localStorage.getItem("accessibility-widget-bigCursor-variant") ||
							"default";
						applyBigCursorStyle(variant);
					}
				}

				if (key === "screenReader") {
					document.addEventListener("mouseup", handleTextSelection);
				}
				if (key === "readingGuide") {
					enableReadingGuide();
				}
				if (key === "readingMask") {
					createReadingMask();
				}
				if (key === "tooltips") {
					enableTooltips();
				}
				if (key === "highlightHover") {
					enableHighlightHover();
				}
				if (key === "magnifiImages") {
					enableMagnifiImages();
				}
				if (key === "pointerTrail") {
					try {
						const v = localStorage.getItem(
							"accessibility-widget-pointerTrail-variant",
						);
						if (v) {
							applyPointerTrailVariant(v);
						} else {
							enablePointerTrail();
						}
					} catch {
						enablePointerTrail();
					}
				}
				if (key === "clickSpark") {
					createClickSparkIfMissing();
				}
				if (key === "imageTrails") {
					createImageTrailsIfMissing();
				}
				// bigCursor handled above
			}
		});
		// Ensure profile state reflects persisted value (if any)
		const storedProfile =
			typeof window !== "undefined"
				? localStorage.getItem("accessibility-widget-active-profile")
				: null;
		if (storedProfile) setActiveProfile(storedProfile);
	}, []);

	// Cross-instance sync listener (multiple components using the hook)
	useEffect(() => {
		const sync = () => {
			setActiveFeatures(getActiveFeatures());
			// sync active profile from storage (or null)
			if (typeof window !== "undefined") {
				const stored = localStorage.getItem(
					"accessibility-widget-active-profile",
				);
				setActiveProfile(stored || null);
			}
			setVersion((v) => v + 1);
		};
		window.addEventListener("accessibility-features-changed", sync);
		window.addEventListener("accessibility-profile-changed", sync);
		window.addEventListener("storage", sync); // multi-tab
		return () => {
			window.removeEventListener("accessibility-features-changed", sync);
			window.removeEventListener("accessibility-profile-changed", sync);
			window.removeEventListener("storage", sync);
		};
	}, []);

	const handleToggle = (key) => {
		const config = featureConfig[key];
		if (!config) return console.warn(`No config for ${key}`);

		const active = getActiveFeatures();
		const isActive = activeFeatures.includes(key);
		if (key === "screenReader") {
			document.addEventListener("mouseup", handleTextSelection);
		}
		if (config.type === "style") {
			if (isActive) {
				const updated = activeFeatures.filter((f) => f !== key);
				removeStyle(`accessibility-widget-style-${key}`);
				saveActiveFeatures(updated);
				setActiveFeatures(updated);
			} else {
				const updated = [...activeFeatures, key];
				injectStyle(`accessibility-widget-style-${key}`, config.css);
				saveActiveFeatures(updated);
				setActiveFeatures(updated);
			}
		}

		if (config.type === "js" && typeof config.handler === "function") {
			// For JS features, always call the handler (it manages its own steps)
			config.handler();
			// After that, get the updated active features list and sync state
			const updatedActive = getActiveFeatures();
			setActiveFeatures(updatedActive);
			// Add to active features if not already active
			// if (!isActive) {
			// 	saveActiveFeatures([...activeFeatures, key]);
			// 	setActiveFeatures([...activeFeatures, key]);
			// }
		}
	};
	const buttonHandler = (key, dir = "inc") => {
		// in ui i have + and - button  now make it for apply for all key
		const current = getFeatureStep(key);
		const next = dir === "inc" ? current + 1 : current - 1;
		saveFeatureStep(key, next);
		const active = getActiveFeatures();
		if (!active.includes(key)) {
			saveActiveFeatures([...active, key]);
		}

		if (key === "contentScaling") {
			applyBodyZoom(next);
		}
		if (key === "contrast") {
			applyContrastMode(next);
		}
		setVersion((v) => v + 1);
	};

	const isActive = (key) => getActiveFeatures().includes(key);

	const resetAll = () => {
		setActiveProfile(null);
		if (typeof window !== "undefined") {
			localStorage.removeItem("accessibility-widget-active-profile");
			window.dispatchEvent(new Event("accessibility-profile-changed"));
		}
		const activeFeaturesList = getActiveFeatures();

		activeFeaturesList.forEach((key) => {
			const config = featureConfig[key];

			if (config?.type === "style") {
				removeStyle(`accessibility-widget-style-${key}`);
			}

			// Handle JS-based features
			if (config?.type === "js") {
				switch (key) {
					case "contrast":
						removeStyle("accessibility-widget-style-contrast");
						break;
					case "contentScaling":
						applyBodyZoom(-1);
						removeStyle("accessibility-widget-style-contentScaling");
						break;
					case "screenReader":
						document.removeEventListener("mouseup", handleTextSelection);
						stopSpeaking(); // hard stop
						// optional confirmation kept short
						speak("Screen reader deactivated");
						break;
					case "readingGuide":
						disableReadingGuide();
						break;
					case "readingMask":
						removeReadingMask();
						break;
					case "adjustFontSizing":
						applyFontSizing(-1);
						removeStyle("accessibility-widget-style-fontSizing");
						break;
					case "adjustLineHeight":
						applyLineHeight(-1);
						removeStyle("accessibility-widget-style-adjustLineHeight");
						break;
					case "adjustLetterSpacing":
						applyLetterSpacing(-1);
						removeStyle("accessibility-widget-style-adjustLetterSpacing");
						break;
					case "contentAlignment":
						removeStyle("accessibility-widget-style-contentAlignment");
						break;
					case "colorSaturation":
						if (document.documentElement) {
							document.documentElement.style.filter = "none";
						}
						document.body.style.filter = "none"; // legacy
						removeStyle("accessibility-widget-style-colorSaturation");
						break;
					case "tooltips":
						if (window.__accessibilityTooltipCleanup) {
							window.__accessibilityTooltipCleanup();
							delete window.__accessibilityTooltipCleanup;
						}
						break;
					case "highlightHover":
						if (window.__highlightHoverCleanup) {
							window.__highlightHoverCleanup();
							delete window.__highlightHoverCleanup;
						}
						break;
					case "magnifiImages":
						if (window.__magnifiImagesCleanup) {
							window.__magnifiImagesCleanup();
							delete window.__magnifiImagesCleanup;
						}
						break;
					case "pointerTrail":
						if (window.__pointerTrailCleanup) {
							window.__pointerTrailCleanup();
							delete window.__pointerTrailCleanup;
						}
						break;
					case "clickSpark":
						if (window.__clickSparkCleanup) {
							window.__clickSparkCleanup();
						}
						break;
					case "bigCursor":
						removeStyle("accessibility-widget-style-bigCursor");
						localStorage.removeItem("accessibility-widget-bigCursor-size");
						break;
					case "imageTrails":
						if (window.__imageTrailsCleanup) {
							window.__imageTrailsCleanup();
						}
						break;
				}
			}
		});

		// Reset font scaling attributes
		const elements = document.querySelectorAll("[data-base-font-size]");
		elements.forEach((el) => {
			const baseSize = el.getAttribute("data-base-font-size");
			const baseLine = el.getAttribute("data-base-line-height");
			if (baseSize) el.style.fontSize = `${baseSize}px`;
			if (baseLine) el.style.lineHeight = `${baseLine}px`;
		});

		// Clear localStorage
		localStorage.removeItem("accessibility-widget-active-features");
		localStorage.removeItem("accessibility-widget-feature-steps");
		localStorage.removeItem("accessibility-widget-page-structure-data");
		localStorage.removeItem("accessibility-widget-pointerTrail-color");
		localStorage.removeItem("accessibility-widget-bigCursor-variant");
		localStorage.removeItem("accessibility-widget-bigCursor-size");

		// Update state
		setActiveFeatures([]);
		setVersion((v) => v + 1);
		if (typeof window !== "undefined") {
			window.dispatchEvent(new Event("accessibility-features-changed"));
		}
	};

	// Apply a predefined accessibility profile
	const applyProfile = (profile) => {
		if (!profile) return;
		// Toggle off if clicking the already active profile
		if (activeProfile === profile.id) {
			resetAll();
			return;
		}
		// 1. Reset everything first (clears previous profile features)
		resetAll();
		// Extra defensive cleanup for root saturation filter after reset
		if (document?.documentElement) {
			document.documentElement.style.filter = "none";
		}
		// 2. Apply each feature defined in the profile sequentially
		const applied = [];
		profile.features?.forEach((f) => {
			const cfg = featureConfig[f.key];
			if (!cfg) return;
			if (cfg.type === "style" && cfg.css) {
				injectStyle(`accessibility-widget-style-${f.key}`, cfg.css);
				applied.push(f.key);
			} else if (cfg.type === "js") {
				// If step-based, set step directly
				if (Array.isArray(cfg.steps) && typeof f.step === "number") {
					// Save step and apply corresponding function
					// Reuse existing handler logic by simulating steps progression
					// Directly call underlying apply functions where possible
					if (f.key === "contentScaling") applyBodyZoom(f.step);
					if (f.key === "contrast") applyContrastMode(f.step);
					if (f.key === "contentAlignment") applyContentAlignment(f.step);
					if (f.key === "colorSaturation") applyColorSaturation(f.step);
					if (f.key === "adjustFontSizing") applyFontSizing(f.step);
					if (f.key === "adjustLineHeight") applyLineHeight(f.step);
					if (f.key === "adjustLetterSpacing") applyLetterSpacing(f.step);
					saveFeatureStep(f.key, f.step);
					applied.push(f.key);
				} else if (typeof cfg.handler === "function") {
					// For toggle JS features (non step-based) ensure they end up active
					const before = getActiveFeatures();
					if (!before.includes(f.key)) {
						cfg.handler();
					}
					const after = getActiveFeatures();
					if (after.includes(f.key)) applied.push(f.key);
				}
			}
		});
		// Persist active features list
		if (applied.length) {
			saveActiveFeatures(applied);
			setActiveFeatures(applied);
		}
		setActiveProfile(profile.id);
		if (typeof window !== "undefined") {
			localStorage.setItem("accessibility-widget-active-profile", profile.id);
			window.dispatchEvent(new Event("accessibility-profile-changed"));
		}
	};

	return {
		handleToggle,
		isActive,
		resetAll,
		getFeatureStep,
		getActiveFeatures,
		saveFeatureStep,
		buttonHandler,
		applyProfile,
		activeProfile,
		activeFeatures, // Add activeFeatures to the return for dependency tracking
	};
}
